import React from 'react';
import type { ReactNode } from 'react';
import { portfolioApi } from '../services/portfolioApi';
import type { User } from '../../../types';
import { AdminModal } from '../../admin/components/AdminModal/AdminModal';
import { UnifiedButton } from '../../../shared/components/UnifiedButton/UnifiedButton';
import * as toolbar from '../../../styles/system/toolbar.css';

export interface LoginOptions {
  preAuth?: boolean;
  twoFactorCode?: string;
  token?: string;
}

export interface LoginResult {
  requiresTwoFactor?: boolean;
  token?: string;
}

export interface AdminContextType {
  user: User | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  clearError: () => void;
  loading: boolean;
}

const AdminContext = React.createContext<AdminContextType | undefined>(undefined);

export const useAdmin = () => {
  const context = React.useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin doit être utilisé dans un AdminProvider');
  }
  return context;
};

interface AdminProviderProps {
  children: ReactNode;
}

export const AdminProvider = ({ children }: AdminProviderProps) => {
  const [user, setUser] = React.useState<User | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [sessionExpired, setSessionExpired] = React.useState(false);

  const isAuthenticated = !!user;
  const isAdmin = user?.isAdmin || user?.is_admin || false;

  // ================================
  // FONCTIONS UTILITAIRES
  // ================================

  const clearError = () => setError(null);

  const saveUserToStorage = (userData: User, token: string) => {
    try {
      localStorage.setItem('auth_token', token);
      localStorage.setItem('portfolio_user', JSON.stringify(userData));
    } catch (err) {
      // Ignore storage errors
    }
  };

  const clearUserFromStorage = () => {
    try {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('portfolio_user');
    } catch (err) {
      // Ignore storage errors
    }
  };

  // ================================
  // authentication
  // ================================

  const login = async (email: string, password: string): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await portfolioApi.auth.login({ email, password });

      if (response.success && response.data) {
        const { user: userData, token } = response.data;

        const normalizedUser: User = {
          id: userData.id,
          email: userData.email,
          first_name: userData.first_name || '',
          last_name: userData.last_name || '',
          is_admin: userData.isAdmin || userData.is_admin || false,
          is_active: true,
          created_at: userData.created_at || new Date().toISOString(),
          updated_at: userData.updated_at || new Date().toISOString(),
          failed_login_attempts: 0,
          is_configured: true,
          // Legacy properties for frontend compatibility
          isAdmin: userData.isAdmin || userData.is_admin || false,
          role: userData.role || 'admin'
        };

        // Sauvegarder les data
        saveUserToStorage(normalizedUser, token);
        setUser(normalizedUser);

      } else {
        const errorMessage = response.error || response.message || 'Échec de la connexion';
        setError(errorMessage);
        throw new Error(errorMessage);
      }

    } catch (err: any) {
      const errorMessage = err.message || 'Erreur de connexion inconnue';
      setError(errorMessage);
      setUser(null);
      clearUserFromStorage();
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      setIsLoading(true);

      await portfolioApi.auth.logout();

    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
      setError(null);
      clearUserFromStorage();
      setIsLoading(false);
    }
  };

  const checkAuth = async (): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      const token = localStorage.getItem('auth_token');
      if (!token) {
        setUser(null);
        return;
      }

      // Optimization - try localStorage first to avoid 429 errors
      const cachedUserStr = localStorage.getItem('portfolio_user');
      if (cachedUserStr) {
        try {
          const cachedUser = JSON.parse(cachedUserStr);
          if (cachedUser && cachedUser.email) {
            const normalizedUser: User = {
              id: cachedUser.id,
              email: cachedUser.email,
              first_name: cachedUser.first_name || '',
              last_name: cachedUser.last_name || '',
              is_admin: cachedUser.isAdmin || cachedUser.is_admin || false,
              is_active: true,
              created_at: cachedUser.created_at || new Date().toISOString(),
              updated_at: cachedUser.updated_at || new Date().toISOString(),
              failed_login_attempts: 0,
              is_configured: true,
              isAdmin: cachedUser.isAdmin || cachedUser.is_admin || false,
              role: cachedUser.role || 'admin'
            };

            setUser(normalizedUser);
          }
        } catch (e) {
          // Ignore cache errors
        }
      }

      // FALLBACK - Si pas de cache, alors faire l'appel API (rarement)
      const verifyResponse = await portfolioApi.auth.verifyToken();

      if (verifyResponse.success && verifyResponse.data?.valid) {
        const userResponse = await portfolioApi.auth.getCurrentUser();

        if (userResponse.success && userResponse.data) {
          const userFromApi = userResponse.data;

          const normalizedUser: User = {
            id: userFromApi.id,
            email: userFromApi.email,
            // FIXED - Use only underscore-based names
            first_name: userFromApi.first_name || '',
            last_name: userFromApi.last_name || '',
            is_admin: userFromApi.isAdmin || userFromApi.is_admin || false,
            is_active: true,
            created_at: userFromApi.created_at || new Date().toISOString(),
            updated_at: userFromApi.updated_at || new Date().toISOString(),
            failed_login_attempts: 0,
            is_configured: true,
            // Legacy properties for compatibility
            isAdmin: userFromApi.isAdmin || userFromApi.is_admin || false,
            role: userFromApi.role || 'admin'
          };

          setUser(normalizedUser);
        } else {
          const userStr = localStorage.getItem('portfolio_user');

          if (userStr) {
            try {
              const userData = JSON.parse(userStr);
              setUser(userData);
            } catch (parseErr) {
              clearUserFromStorage();
              setUser(null);
            }
          } else {
            clearUserFromStorage();
            setUser(null);
          }
        }
      } else {
        clearUserFromStorage();
        setUser(null);
      }

    } catch (err: any) {
      try {
        const userStr = localStorage.getItem('portfolio_user');
        const token = localStorage.getItem('auth_token');

        if (userStr && token) {
          const userData = JSON.parse(userStr);
          setUser(userData);
        } else {
          setUser(null);
        }
      } catch (fallbackErr) {
        clearUserFromStorage();
        setUser(null);
      }

    } finally {
      setIsLoading(false);
    }
  };

  // ================================
  // initialization
  // ================================

  React.useEffect(() => {
    checkAuth();
  }, []);

  // ================================
  // GESTION DU TIMEOUT D'INACTIVITÉ (15 MINUTES)
  // ================================
  React.useEffect(() => {
    if (!isAuthenticated) return;

    const inactivityTimeoutMs = 15 * 60 * 1000;
    let timeoutId: NodeJS.Timeout;

    const handleAutomaticLogout = () => {
      logout();
      setSessionExpired(true);
    };

    const resetTimer = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(handleAutomaticLogout, inactivityTimeoutMs);
    };

    const activityEvents = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];

    activityEvents.forEach((event) => {
      window.addEventListener(event, resetTimer);
    });

    resetTimer();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      activityEvents.forEach((event) => {
        window.removeEventListener(event, resetTimer);
      });
    };
  }, [isAuthenticated]);

  // ================================
  // CONTEXT VALUE
  // ================================

  const contextValue: AdminContextType = {
    user,
    isAuthenticated,
    isAdmin,
    isLoading,
    error,
    login,
    logout,
    checkAuth,
    clearError,
    // Added - alias for compatibility
    loading: isLoading
  };

  return (
    <AdminContext.Provider value={contextValue}>
      {children}
      <AdminModal
        isOpen={sessionExpired}
        onClose={() => setSessionExpired(false)}
        title="Session expirée"
        size="small"
        closeOnBackdropClick={false}
      >
        <p>Votre session a expiré après 15 minutes d'inactivité. Veuillez vous reconnecter.</p>
        <div className={toolbar.actionBar}>
          <UnifiedButton variant="primary" onClick={() => setSessionExpired(false)}>
            Se reconnecter
          </UnifiedButton>
        </div>
      </AdminModal>
    </AdminContext.Provider>
  );
};

export { AdminContext };
export default AdminProvider;

// Hooks exported after context definition to prevent initialization issues
export const useAuthState = () => {
  const { user, isAuthenticated, isLoading } = useAdmin();
  return {
    user,
    isAuthenticated,
    isLoading,
    isAdmin: user?.isAdmin || false
  };
};

export const useAdminPermissions = () => {
  const { user } = useAdmin();

  const canManageContent = user?.isAdmin || user?.role === 'admin';
  const canManageUsers = user?.isAdmin;
  const canViewAnalytics = user?.isAdmin || user?.role === 'admin';

  return {
    canManageContent,
    canManageUsers,
    canViewAnalytics,
    isAdmin: user?.isAdmin || false
  };
};
